import { Component } from '@theme/component';

/**
 * La capa VISIBLE de un selector de talle. El control de verdad sigue siendo un `<select>` nativo
 * que vive **fuera** de este elemento, oculto, y este componente sólo lo maneja.
 *
 * Que el `<select>` quede afuera no es un detalle de acomodo: los atributos `on:` de
 * `@theme/component` resuelven `/metodo` contra el componente ANCESTRO MÁS CERCANO. Con el
 * `<select>` adentro, su `on:change="/onPackSizeChange/N"` habría empezado a resolver contra este
 * componente en vez de contra `cart-items-component`, y el cambio de talle habría dejado de llegar
 * al carrito sin un solo error. Dejándolo afuera, el contrato de los dos handlers que ya existen
 * —`onPackSizeChange` en el carrito y `handleGiftSizeChange` en la ficha— queda intacto: cambia la
 * forma del control, no quién lo escucha.
 *
 * Por el mismo mecanismo, los botones de opción viven dentro de `anchored-popover-component` y su
 * `on:click` NO puede escribirse como `/selectOption`: resolvería contra el popover. Van con la
 * forma `#id/metodo`, que `component.js` resuelve por `querySelector` y permite invocar un
 * componente que no es ancestro.
 *
 * @extends Component
 */
export class PackSizeDropdown extends Component {
  /** El `<select>` que este control maneja. */
  get native() {
    const id = this.dataset.native;
    const element = id ? document.getElementById(id) : null;
    return element instanceof HTMLSelectElement ? element : null;
  }

  /**
   * Aplica la opción tocada: la escribe en el `<select>` y le dispara `change`, que es lo que
   * despierta al handler de siempre. El evento se emite a mano porque asignar `value` por script
   * no dispara `change` en ningún navegador.
   * @param {Event} event
   */
  selectOption(event) {
    const option = event.target instanceof Element ? event.target.closest('[data-value]') : null;
    if (!(option instanceof HTMLElement)) return;

    const value = option.dataset.value ?? '';
    const native = this.native;
    if (!native || !value) return;

    // Cerrar SIEMPRE, incluso si el talle no cambió: si no, tocar la opción ya elegida deja el
    // panel abierto y se lee como que el control se trabó.
    this.#close();

    if (native.value === value) return;

    native.value = value;
    // El rótulo del disparador se pinta acá y no se espera al repintado del servidor: entre el
    // toque y la respuesta del carrito pasa casi un segundo, y sin esto el control sigue mostrando
    // el talle viejo todo ese rato.
    this.#paintTrigger(value, option.textContent ?? value);
    native.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Repinta qué talles se pueden elegir. Lo llama quien SÍ sabe de stock —la ficha, después de cada
   * cambio de carrito— porque este control es compartido y no tiene por qué saber que existe el
   * inventario: sólo sabe pintar y avisar.
   *
   * Se toca el `<select>` oculto Y el panel visible: el primero es el control de verdad (lo que un
   * `change` termina mandando) y el segundo es lo que el cliente ve. Dejar uno de los dos sin
   * actualizar es exactamente cómo se llega a que la UI muestre un talle que el servidor rechaza.
   *
   * @param {Record<string, {available: boolean, note?: string}>} states - por valor de talle
   * @returns {string | null} El talle al que hubo que saltar porque el elegido dejó de estar, o
   *   `null` si no hizo falta mover nada.
   */
  setAvailability(states) {
    const native = this.native;
    if (!native) return null;

    for (const option of native.options) {
      const state = states[option.value];
      if (!state) continue;
      // El talle ELEGIDO nunca se deshabilita acá: un `<select>` con la opción seleccionada
      // deshabilitada queda en un estado que el navegador dibuja distinto en cada plataforma. Si
      // dejó de estar disponible, abajo se salta a otro y recién ahí se apaga.
      option.disabled = !state.available && option.value !== native.value;
    }

    for (const button of this.querySelectorAll('[data-value]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      const state = states[button.dataset.value ?? ''];
      if (!state) continue;
      button.disabled = !state.available;
      const note = button.querySelector('.pack-size-field__option-note');
      if (note) note.textContent = state.available ? '' : state.note ?? 'sin stock';
    }

    if (states[native.value]?.available !== false) return null;

    // El elegido se quedó sin stock: se salta al primero que sí tenga. Es la misma regla que ya
    // aplica la tarjeta de pack cuando la marcada se apaga — nunca queda elegido algo que el
    // servidor va a rechazar.
    const rescue = [...native.options].find((option) => states[option.value]?.available);
    if (!rescue) return null;

    native.value = rescue.value;
    this.#paintTrigger(rescue.value, rescue.value);
    for (const option of native.options) {
      const state = states[option.value];
      if (state) option.disabled = !state.available;
    }
    return rescue.value;
  }

  /**
   * @param {string} value
   * @param {string} label
   */
  #paintTrigger(value, label) {
    const target = this.querySelector('.pack-size-field__value');
    if (target) target.textContent = label.trim() || value;

    for (const option of this.querySelectorAll('[data-value]')) {
      option.setAttribute('aria-selected', String(option instanceof HTMLElement && option.dataset.value === value));
    }
  }

  #close() {
    const popover = this.querySelector('[popover]');
    // `hidePopover` lanza si el popover no está abierto, así que se pregunta primero. Pasa de
    // verdad: un clic con teclado puede llegar después de que el navegador ya lo cerró.
    if (popover instanceof HTMLElement && popover.matches(':popover-open')) popover.hidePopover();
  }
}

if (!customElements.get('pack-size-dropdown')) {
  customElements.define('pack-size-dropdown', PackSizeDropdown);
}
